import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {ForeignId, ListResponse, SingleResult, UploadedFile, UploadedFileDataType, WeekInfo} from 'utils/types';
import {addResultCases, removeUndefined} from 'utils/helpers';
import ApiService from 'redux/services/api-service';
import {navigationActions} from '../navigation/slice';

export type Configuration = {
  formatId: string;
  files: string[];
  requirements: {require: string; forceRawValue: boolean; mapped?: string | null}[];
  columns: {
    title: string;
    code: string;
    sampleValue: string;
    adapter?: {type: 'requirement'; as: string} | {type: 'var'; as: 'store' | 'item'};
  }[];
};
export type FileImportMeta = {
  id: number;
  uploaded_file: number;
  week_start: string;
  week_end: string;
  gaps: string[];
  total_stores: number;
  test_stores_present: number;
  control_stores_present: number;
  excluded_stores_present: number;
  test_stores_missing: number;
  control_stores_missing: number;
  excluded_stores_missing: number;
  missing_from_test_or_control_selections: string[];
  newly_added_stores: string[];
  missing_from_hero_selections: string[];
  hero_items_present: number;
  missing_from_halo_selections: string[];
  halo_items_present: number;
  newly_added_items: string[];
  created_at: string;
  updated_at: string;
};

interface IState {
  latestTask: any;
  editConfiguration?: Configuration;
  configurations: Configuration[];
  loading: boolean;
  hasError: boolean;
  error: object | null;
  fileList: ListResponse<UploadedFile>;
  sampleRows: SingleResult<{data: any[]; columns: {title: string; name: string; type?: string}[]}>;
  fallbackRows: SingleResult<{data: any[]; columns: {title: string; name: string; type?: string}[]}>;
  requiredWeeks: SingleResult<WeekInfo>;
  replicaState: SingleResult<{
    num_sales_rows_main_database: number;
    num_sales_rows_replica: number;
  }>;
  filePrepareLogs: SingleResult<{messages: string[]}>;
  fileImportMeta: SingleResult<FileImportMeta>;
  fileImportMetaByFiles: SingleResult<Record<string, FileImportMeta | null>>;
}
export const dataLoadingEndpoints = {
  createFileEntry: createAsyncThunk('dataLoading/createFileEntry', async (payload: any) => {
    const response = await ApiService.post('/uploaded_files/', payload);
    return response.data as UploadedFile;
  }),
  updateFileEntry: createAsyncThunk('dataLoading/updateFileEntry', async (payload: any) => {
    const response = await ApiService.patch(`/uploaded_files/${payload.id}/`, payload);
    return response.data;
  }),
  deleteFileEntry: createAsyncThunk('dataLoading/deleteFileEntry', async (payload: any) => {
    const response = await ApiService.delete(`/uploaded_files/${payload.id}/`);
    return response.data;
  }),
  getFileList: createAsyncThunk(
    'dataLoading/getFileList',
    async (params: {sales_dataset_id: ForeignId; data_type: UploadedFileDataType; offset?: number; limit?: number}) => {
      const cleanParams = {...params};
      removeUndefined(cleanParams);
      const response = await ApiService.get(`/uploaded_files/?${new URLSearchParams(cleanParams as any)}`);
      return response.data;
    },
  ),
  getRequiredWeeksOfData: createAsyncThunk(
    'dataLoading/getRequiredWeeksOfData',
    async (params: {
      campaign_id: ForeignId;
      limitToConfiguredCampaignDates?: boolean;
    }): Promise<SingleResult<WeekInfo>> => {
      const query = params.limitToConfiguredCampaignDates ? '?limit_to_configured_campaign_dates=true' : '';
      const response = await ApiService.get<WeekInfo>(`/campaign_week_endings/${params.campaign_id}${query}`);
      return {as: 'success', value: response.data};
    },
  ),
  getUploadUrl: createAsyncThunk('dataLoading/getUploadUrl', async (key: string) => {
    const response = await ApiService.get(`/get_data_loading_upload_url/?path=${key}`);
    return response.data;
  }),
  getSampleRows: createAsyncThunk('dataLoading/getSampleRows', async (params: any) => {
    try {
      const response = await ApiService.get(
        `/file_adapter_sample_data/${params.uploaded_file_id}/${params.selected_file_adapter_id}/`,
      );
      return {as: 'success', value: response.data};
    } catch (err: any) {
      return {as: 'error', error: 'Error Occurred On Server. Cannot Load Sample data'};
    }
  }),
  getFallbackSampleRows: createAsyncThunk('dataLoading/getFallbackSampleRows', async (params: any) => {
    try {
      const response = await ApiService.get(`/fallback_sample_data/${params.uploaded_file_id}/`);
      return {as: 'success', value: response.data};
    } catch (err: any) {
      return {as: 'error', error: 'Error Occurred On Server. Cannot Load Sample data'};
    }
  }),
  getFilePrepareLogs: createAsyncThunk('dataLoading/getFilePrepareLogs', async (params: any) => {
    const response = await ApiService.get(`/file_prepare_logs/${params.uploaded_file_id}/`);
    return {as: 'success', value: response.data};
  }),
  startSalesImportTask: createAsyncThunk('dataLoading/startSalesImportTask', async (params: any) => {
    const response = await ApiService.post(
      `/process_uploaded_confirmed_files/${params.sales_dataset_id}/${params.dataType}/`,
    );
    return response.data;
  }),
  reProcessFile: createAsyncThunk('dataLoading/reProcessFile', async (payload: any) => {
    const response = await ApiService.post('/process_uploaded_confirmed_files/re_process_file/', payload);
    return response.data;
  }),
  getFileAdapters: createAsyncThunk('dataLoading/getFileAdapters', async (search: string) => {
    const response = await ApiService.get(`/file_adapter/?search=${search}`);
    return response.data;
  }),
  getSalesDatasetReplicaState: createAsyncThunk(
    'dataLoading/getSalesDatasetReplicaState',
    async (params: {sales_dataset_id: number | string | undefined}) => {
      try {
        const response = await ApiService.get(
          `/sales_dataset_replica_state/?sales_dataset_id=${params.sales_dataset_id}`,
        );
        return {as: 'success', value: response.data};
      } catch (err: any) {
        return {as: 'error', error: 'Error Occurred On Server. Cannot Load Sales Dataset Replica State.'};
      }
    },
  ),
  getFileImportMeta: createAsyncThunk(
    'dataLoading/getFileImportMeta',
    async (params: {uploaded_file_id: number | string; campaign_id: number | string}) => {
      try {
        const response = await ApiService.get(
          `/file_import_meta/by-uploaded-file/${params.uploaded_file_id}/?campaign_id=${params.campaign_id}`,
        );
        return {as: 'success', value: response.data};
      } catch (err: any) {
        return {as: 'error', error: 'Error Occurred On Server. Cannot Load File Import Meta.'};
      }
    },
  ),
  getFileImportMetaByUploadedFiles: createAsyncThunk(
    'dataLoading/getFileImportMetaByUploadedFiles',
    async (params: {uploaded_file_ids: (number | string)[]; campaign_id: number | string}) => {
      try {
        const ids = params.uploaded_file_ids.join(',');
        const response = await ApiService.get(
          `/file_import_meta/by-uploaded-files/?ids=${ids}&campaign_id=${params.campaign_id}`,
        );
        return {as: 'success', value: response.data as Record<string, FileImportMeta | null>};
      } catch (err: any) {
        return {as: 'error', error: 'Error Occurred On Server. Cannot Load File Import Meta.'};
      }
    },
  ),
};
const initialState = {
  latestTask: null,
  editConfiguration: undefined,
  configurations: [],
  loading: false,
  hasError: false,
  error: null,
  fileList: {
    count: 0,
    results: [],
  },
  requiredWeeks: {as: 'idle'},
  fallbackRows: {as: 'idle'},
  sampleRows: {as: 'idle'},
  replicaState: {as: 'idle'},
  filePrepareLogs: {as: 'idle'},
  fileImportMeta: {as: 'idle'},
  fileImportMetaByFiles: {as: 'idle'},
} satisfies IState;
export const dataLoadingSlice = createSlice({
  name: 'dataLoading',
  initialState: {
    ...initialState,
  } as IState,
  reducers: {
    editConfiguration(state, {payload}: {payload: Configuration}) {
      state.editConfiguration = payload;
    },
    setRequirement(state, {payload}: {payload: {requirement: string; columnCode: string}}) {
      const {requirement, columnCode} = payload;
      const column = state.editConfiguration?.columns.find(it => it.code === columnCode);
      const targetRequirement = state.editConfiguration?.requirements.find(it => it.require === requirement);
      const currentMappedRequirement = state.editConfiguration?.requirements.find(it => it.mapped === columnCode);

      if (!targetRequirement || !column) {
        throw Error('Failed to set requirement');
      }
      const oldRequirement = targetRequirement.mapped;
      targetRequirement.mapped = columnCode;
      targetRequirement.forceRawValue = false;
      column.adapter = {
        type: 'requirement',
        as: requirement,
      };

      if (oldRequirement) {
        const oldRequirementColumn = state.editConfiguration?.columns.find(it => it.code === oldRequirement);
        // if (!oldRequirementColumn) {
        //   throw Error('Failed to set requirement');
        // }
        if (oldRequirementColumn) {
          oldRequirementColumn.adapter = undefined;
        }
      }
      if (currentMappedRequirement) {
        currentMappedRequirement.mapped = null;
      }
    },
    setRequirementCustom(state, {payload}: {payload: {requirement: string; columnRawValue: string}}) {
      const {requirement, columnRawValue} = payload;

      const targetRequirement = state.editConfiguration?.requirements.find(it => it.require === requirement);

      if (!targetRequirement) {
        throw Error('Failed to set target requirement override');
      }
      const oldRequirement = targetRequirement.mapped;
      targetRequirement.mapped = columnRawValue;
      targetRequirement.forceRawValue = true;

      if (oldRequirement) {
        const oldRequirementColumn = state.editConfiguration?.columns.find(it => it.code === oldRequirement);
        // if (!oldRequirementColumn) {
        //   throw Error('Failed to set old requirement override');
        // }
        if (oldRequirementColumn) {
          oldRequirementColumn.adapter = undefined;
        }
      }
    },
    setAttributeDefinition(state, {payload}) {
      const {code, definition} = payload;
      const attribute = state.editConfiguration?.columns.find(it => it.code === code);
      if (!attribute) {
        throw Error('Attribute not found');
      }
      if (definition === 'ignore') {
        delete attribute.adapter;
      } else {
        attribute.adapter = {
          type: 'var',
          as: definition,
        };
      }
      // attribute.definition = definition;
    },
    applyConfigurationEdits(state, {payload}) {
      state.configurations = JSON.parse(JSON.stringify(payload));
    },
    clearLastProcessingTask(state) {
      state.latestTask = null;
    },
    reset() {
      return {...initialState};
    },
  },
  extraReducers: builder => {
    addResultCases(builder, dataLoadingEndpoints.getRequiredWeeksOfData, 'requiredWeeks');
    addResultCases(builder, dataLoadingEndpoints.getFallbackSampleRows, 'fallbackRows');
    addResultCases(builder, dataLoadingEndpoints.getSampleRows, 'sampleRows');
    addResultCases(builder, dataLoadingEndpoints.getSalesDatasetReplicaState, 'replicaState');
    addResultCases(builder, dataLoadingEndpoints.getFilePrepareLogs, 'filePrepareLogs');
    addResultCases(builder, dataLoadingEndpoints.getFileImportMeta, 'fileImportMeta');
    addResultCases(builder, dataLoadingEndpoints.getFileImportMetaByUploadedFiles, 'fileImportMetaByFiles');
    builder.addCase(dataLoadingEndpoints.getFileList.pending, state => {
      state.loading = true;
      state.fileList.results = [...state.fileList.results];
    });
    builder.addCase(dataLoadingEndpoints.getFileList.fulfilled, (state, {payload}) => {
      state.loading = false;
      state.hasError = false;
      state.fileList = {results: payload.results, count: payload.count};
    });
    builder.addCase(dataLoadingEndpoints.getFileList.rejected, (state, {payload}) => {
      state.loading = false;
      state.hasError = true;
      state.error = payload as object;
      state.fileList = {results: [], count: 0};
    });
    builder.addCase(navigationActions.setContextCampaign, () => {
      return {...initialState};
    });
  },
});

export const dataLoadingActions = dataLoadingSlice.actions;

export type DataLoadingSlice = {
  [dataLoadingSlice.name]: ReturnType<(typeof dataLoadingSlice)['reducer']>;
};
